/**
 * Edit form for a school year.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { DatePicker } from '@/components/forms/DatePicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { getSchoolYear, getSchoolYears, updateSchoolYear } from '@/services/school-years.service';
import type { UpdateSchoolYearDto } from '@/types/school-year.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { schoolYearValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useAuthStore } from '@/stores/authStore';
import { SCHOOL_YEAR_FORM_HINTS, SCHOOL_YEAR_FORM_QUERY_KEYS } from './school-year-configs';
import {
  applySchoolYearFormField,
  buildUpdateSchoolYearDto,
  formatEstablishmentObjectiveInput,
  parseEstablishmentObjective,
  schoolYearToFormData,
  withSchoolYearIntegrityContext,
} from './school-year-form-utils';

/** Props for {@link EditSchoolYearView}. */
interface EditSchoolYearViewProps {
  schoolYearId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for a school year. */
export const EditSchoolYearView: React.FC<EditSchoolYearViewProps> = ({
  schoolYearId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<UpdateSchoolYearDto>({});

  const validation = useFormValidation(schoolYearValidationSchema);
  const { roles: currentUserRoles } = useAuthStore();
  const currentUserRoleCode = currentUserRoles?.[0]?.code;
  const { data: yearsResponse } = useQuery({
    queryKey: SCHOOL_YEAR_FORM_QUERY_KEYS.integrity,
    queryFn: () => getSchoolYears({ page: 1, pageSize: 200 }),
  });
  const existingYears = yearsResponse?.data ?? [];

  const withIntegrityContext = React.useCallback(
    (data: UpdateSchoolYearDto) =>
      withSchoolYearIntegrityContext(data, existingYears, schoolYearId),
    [existingYears, schoolYearId],
  );

  const {
    data: schoolYear,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: SCHOOL_YEAR_FORM_QUERY_KEYS.one(schoolYearId),
    queryFn: () => getSchoolYear(schoolYearId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'schoolYearNotFound',
    onClose,
    entityId: schoolYearId,
  });

  /** Hydrate and run initial validation once GET school year resolves. */
  React.useEffect(() => {
    if (!schoolYear) return;
    const initialData = schoolYearToFormData(schoolYear);
    setFormData(initialData);
    validation.validateInitialFormData(withIntegrityContext(initialData));
  }, [schoolYear]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateSchoolYearDto) => updateSchoolYear(schoolYearId, dto),
    onSuccess: () => {
      toast.success('Année scolaire modifiée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Patch one field and re-validate against existing years. */
  const handleFieldChange = (
    field: keyof UpdateSchoolYearDto,
    value: string | boolean | Date | number | undefined,
  ) => {
    const updatedData = applySchoolYearFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, updatedData[field], withIntegrityContext(updatedData));
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate overlap then PATCH update DTO (objective is admin-only). */
  const handleSubmit = () => {
    if (!validation.validateForm(withIntegrityContext(formData))) return;
    updateMutation.mutate(buildUpdateSchoolYearDto(formData, currentUserRoleCode));
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier l'année scolaire"
      subtitle="Modifiez les informations de l'année scolaire"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
      isLoading={isLoading || (!!schoolYear && Object.keys(formData).length === 0)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <Input
          label="Code"
          value={formData.code || ''}
          onChange={(e) => handleFieldChange('code', e.target.value)}
          onBlur={() => handleBlur('code')}
          placeholder="Ex: 2025-2026"
          maxLength={20}
          required
          helperText={SCHOOL_YEAR_FORM_HINTS.code}
          state={validation.getFieldState('code')}
          error={validation.getFieldError('code')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Date de début"
            value={formData.startDate || ''}
            onChange={(value) => handleFieldChange('startDate', value)}
            onBlur={() => handleBlur('startDate')}
            required
            helperText={SCHOOL_YEAR_FORM_HINTS.startDate}
            state={validation.getFieldState('startDate')}
            error={validation.getFieldError('startDate')}
          />

          <DatePicker
            label="Date de fin"
            value={formData.endDate || ''}
            onChange={(value) => handleFieldChange('endDate', value)}
            onBlur={() => handleBlur('endDate')}
            minDate={formData.startDate}
            required
            helperText={SCHOOL_YEAR_FORM_HINTS.endDate}
            state={validation.getFieldState('endDate')}
            error={validation.getFieldError('endDate')}
            align="right"
          />
        </div>

        <Checkbox
          label="Année en cours"
          checked={formData.isCurrent || false}
          onCheckedChange={(checked) => handleFieldChange('isCurrent', checked)}
        />

        {currentUserRoleCode === 'admin' && (
          <Input
            label="Objectif établissement"
            type="number"
            min={0}
            value={formatEstablishmentObjectiveInput(formData.establishmentObjective)}
            onChange={(e) => {
              handleFieldChange(
                'establishmentObjective',
                parseEstablishmentObjective(e.target.value),
              );
            }}
            placeholder="Ex: 50"
            helperText="Nombre d'établissements visé pour cette année scolaire (admin uniquement)"
          />
        )}
      </div>
    </SlidePanelForm>
  );
};
